import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Helmet } from 'react-helmet-async';
import { Lock, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Passwort-Setup-Seite für automatisch angelegte Firmen (Digistore24-Kauf).
 * Kunden erhalten per E-Mail einen Link `/setup-password?token=XYZ`.
 * Nach dem Setup werden sie direkt eingeloggt.
 */
const SetupPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const isWelcome = searchParams.get('welcome') === '1';

  const [status, setStatus] = useState('verifying'); // verifying | ready | expired | done
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('expired');
      setErrMsg('Kein Setup-Token in der URL gefunden.');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/setup-verify/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!res.ok) {
          setStatus('expired');
          setErrMsg(data.detail || 'Setup-Link ungültig');
          return;
        }
        setEmail(data.email || '');
        setName(data.name || '');
        setStatus('ready');
      } catch (err) {
        setStatus('expired');
        setErrMsg('Netzwerkfehler bei Token-Prüfung: ' + err.message);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    if (pw1.length < 8) {
      setErrMsg('Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    if (pw1 !== pw2) {
      setErrMsg('Passwörter stimmen nicht überein.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password: pw1 }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setErrMsg(data.detail || 'Setup fehlgeschlagen');
        return;
      }
      // Cookie wurde vom Backend gesetzt (sv_session + sv_csrf).
      // KEIN localStorage.setItem mehr — Session lebt ausschließlich im httpOnly-Cookie.
      toast.success('Passwort gesetzt', { description: 'Sie sind nun angemeldet.' });
      setStatus('done');
      // Full reload, damit AuthContext.checkAuth() die neue Cookie-Session übernimmt
      setTimeout(() => window.location.assign('/dashboard'), 800);
    } catch (err) {
      setErrMsg('Netzwerkfehler: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center px-4">
      <Helmet>
        <title>Passwort einrichten – SmartVolt Academy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Card className="w-full max-w-md bg-gray-900/80 border-yellow-500/30" data-testid="setup-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2" data-testid="setup-title">
            <Lock className="h-5 w-5 text-yellow-400" /> {isWelcome ? 'Willkommen bei SmartVolt Academy!' : 'Firmenkonto einrichten'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {isWelcome
              ? 'Ihr 14-Tage-Testzugang ist aktiv. Legen Sie jetzt Ihr Passwort fest – danach landen Sie direkt im Chef-Cockpit und können Ihre Mitarbeiter einladen.'
              : 'Legen Sie Ihr Passwort für SmartVolt Academy fest.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'verifying' && (
            <div className="py-6 text-center text-gray-400 flex flex-col items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" /> Link wird geprüft…
            </div>
          )}
          {status === 'expired' && (
            <div className="text-center py-6 space-y-3" data-testid="setup-expired">
              <AlertTriangle className="h-10 w-10 text-rose-400 mx-auto" />
              <p className="text-rose-300 font-semibold">Setup-Link ungültig</p>
              <p className="text-sm text-gray-400">{errMsg || 'Der Link ist abgelaufen oder wurde bereits eingelöst.'}</p>
              <Button onClick={() => navigate('/login')} className="bg-yellow-500 text-black hover:bg-yellow-400">
                Zum Login
              </Button>
            </div>
          )}
          {status === 'ready' && (
            <form onSubmit={submit} className="space-y-3" data-testid="setup-form">
              <div>
                <Label className="text-gray-300 text-sm">Firmen-Email</Label>
                <Input value={email} disabled className="bg-black/40 border-gray-700 text-gray-300" />
              </div>
              {name && (
                <div>
                  <Label className="text-gray-300 text-sm">Inhaber</Label>
                  <Input value={name} disabled className="bg-black/40 border-gray-700 text-gray-300" />
                </div>
              )}
              <div>
                <Label className="text-gray-300 text-sm">Neues Passwort</Label>
                <Input
                  type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
                  className="bg-black/40 border-gray-700 text-white" data-testid="setup-pw1"
                  placeholder="Mindestens 8 Zeichen" autoComplete="new-password" required
                />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Passwort wiederholen</Label>
                <Input
                  type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                  className="bg-black/40 border-gray-700 text-white" data-testid="setup-pw2"
                  autoComplete="new-password" required
                />
              </div>
              {errMsg && (
                <p className="text-sm text-rose-300 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" /> {errMsg}
                </p>
              )}
              <Button type="submit" disabled={submitting}
                className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold hover:from-yellow-400 hover:to-amber-400"
                data-testid="setup-submit">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichere…</> : 'Passwort setzen & einloggen'}
              </Button>
            </form>
          )}
          {status === 'done' && (
            <div className="text-center py-6" data-testid="setup-done">
              <CheckCircle className="h-10 w-10 text-emerald-400 mx-auto" />
              <p className="text-emerald-300 font-semibold mt-2">Passwort gespeichert</p>
              <p className="text-sm text-gray-400">Sie werden zum Dashboard weitergeleitet…</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupPasswordPage;
